import { useCallback, useRef, useState } from 'react';
import {
  IconStatusCorrect,
  ListItem,
  Stack,
  Text,
  useToast,
} from '@adc/parallax-component-library';

import type { DepartmentDetails, PatientData } from '~/api/types';
import { PatientSettingsForm } from '~/components/forms/patientSettings/PatientSettingsForm';
import { Modal } from '~/components/Modal';
import { useModal } from '~/features/modals/useModal';
import type {
  PatientSettingsFormRef,
  PatientSettingsFormValues,
} from '~/features/patientCreation/types';
import type { FileRouteTypes } from '~/routeTree.gen';
import { buildTestId } from '~/utils/buildTestId';
import { formatDurationFromMinutes } from '~/utils/formatDurationFromMinutes';

type PatientEditAlertFormModalProps = {
  testID: string;
  alertSettings: Omit<DepartmentDetails['alertsSettings'], 'alertPhone'>;
  patientId: PatientData['id'];
  onCloseRedirectPath: FileRouteTypes['to'];
  onActionComplete?: () => void;
};

export const PatientEditAlertFormModal = ({
  alertSettings,
  testID,
  onActionComplete,
}: PatientEditAlertFormModalProps) => {
  const { closeModal } = useModal();
  const { showToast } = useToast();
  const [hasFormChanged, setHasFormChanged] = useState(false);
  const [formValues, setFormValues] = useState<PatientSettingsFormValues>();
  const [mode, setMode] = useState<'update' | 'confirm'>('update');

  const settingsFormRef = useRef<PatientSettingsFormRef>(null);

  const onFormSubmit = useCallback(
    async (values: PatientSettingsFormValues) => {
      // TODO Add update mutation for patient settings
      await Promise.resolve();

      closeModal('PatientEditAlertForm');
      onActionComplete?.();

      showToast({
        message: 'Patient settings updated',
        'aria-label': 'Patient settings updated',
        icon: <IconStatusCorrect accentColor="$support.positive.default" />,
      });

      return values;
    },
    [showToast, closeModal, onActionComplete],
  );

  return (
    <Modal
      testID={testID}
      title={mode === 'update' ? 'Edit alert settings' : 'Confirm new settings'}
      hasCloseButton
      size="m"
      primaryAction={{
        label: mode === 'update' ? 'Next' : 'Confirm settings',
        disabled: !hasFormChanged,
        onPress: () => {
          if (mode === 'update') {
            setFormValues(settingsFormRef.current?.getValues());
            setMode('confirm');
          } else {
            void settingsFormRef.current?.submitForm();
          }
        },
      }}
      secondaryAction={{
        label: mode === 'update' ? 'Cancel' : 'Back',
        onPress: () => {
          if (mode === 'update') {
            closeModal('PatientEditAlertForm');
          } else {
            setMode('update');
          }
        },
      }}
      onOpenChange={() => {
        closeModal('PatientEditAlertForm');
      }}
    >
      <Stack
        style={{
          display: mode === 'update' ? 'flex' : 'none',
        }}
      >
        <PatientSettingsForm
          ref={settingsFormRef}
          testID={buildTestId({
            testID,
            suffix: ['settings', 'form'],
          })}
          defaultValues={alertSettings}
          onSubmit={onFormSubmit}
          hasFormChanged={setHasFormChanged}
        />
      </Stack>
      {mode === 'confirm' && formValues && (
        <Stack flex={1} gap="$6" paddingVertical="$8">
          <Text marginBottom="$6">
            Review and confirm these new alert settings.
          </Text>

          <ListItem
            testID={buildTestId({
              testID,
              suffix: ['confirm', 'display', 'lactate', 'data'],
            })}
            padding="$0"
            labelPosition="top"
            label="Display lactate data in patient app"
            title={formValues.displayLactateData ? 'On' : 'Off'}
          />
          <Stack>
            <ListItem
              testID={buildTestId({
                testID,
                suffix: ['confirm', 'high', 'lactate', 'alert'],
              })}
              padding="$0"
              labelPosition="top"
              label="Lactate alert"
              title={formValues.highLactateAlert ? 'On' : 'Off'}
            />
            {formValues.highLactateAlert && (
              <Text type="$bodyBase.medium">
                Trigger alert when lactate reading is at or above{' '}
                <Text type="$bodyBase.strong">
                  {formValues.highLactateAlertAbove} mmol/L
                </Text>{' '}
                for at least{' '}
                <Text type="$bodyBase.strong">
                  {formatDurationFromMinutes(
                    formValues.highLactateAlertDuration,
                  )}
                </Text>
              </Text>
            )}
          </Stack>
        </Stack>
      )}
    </Modal>
  );
};
